import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Undo2,
  Redo2,
  RotateCcw,
  Save,
  Trash2,
  Eye,
  EyeOff,
  Layers,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Flag,
  CornerDownRight,
  ArrowRightLeft,
  Sliders,
  Plus,
  Compass,
  MapPin,
  RefreshCw,
  CheckSquare,
  Grid,
} from 'lucide-react';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';
import type {
  TrackPoint,
  TrackShape,
  TrackAnnotation,
  TrackSegment,
  SplineSample,
} from '@/lib/spline';
import {
  sampleClosedSpline,
  computeRibbonBoundaries,
  findClosestParametricT,
  getPointAtT,
  inferTurnDirectionAtT,
  computeImageGeoBounds,
  applyWidthToRange,
  resetWidthInRange,
  ensureFinishLine,
  renumberCornersByFinishLine,
  bakeTrackSegments,
  findClosestSegmentIndex,
  getTileCoords,
  tileToLat,
  tileToLng,
} from '@/lib/spline';

interface ImageMetadata {
  width: number;
  height: number;
  zoom: number;
  centerLat: number;
  centerLng: number;
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface TrackShapeStudioModalProps {
  trackId: string;
  trackName: string;
  latitude: number;
  longitude: number;
  zoom?: number;
  hasImage?: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type StudioMode = 'spline' | 'corners' | 'sectors' | 'segments';

interface HistorySnapshot {
  shape: TrackShape;
  annotations: TrackAnnotation[];
}

interface TrackContextMenu {
  x: number;
  y: number;
  t: number;
}

export function TrackShapeStudioModal({
  trackId,
  trackName,
  latitude,
  longitude,
  zoom,
  hasImage,
  onClose,
  onSaved,
}: TrackShapeStudioModalProps) {
  const { t } = useI18n();
  const { showToast } = useToast();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Studio state
  const [mode, setMode] = useState<StudioMode>('spline');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Track Shape state
  const [controlPoints, setControlPoints] = useState<TrackPoint[]>([]);
  const [trackWidthM, setTrackWidthM] = useState<number>(8.0);
  const [annotations, setAnnotations] = useState<TrackAnnotation[]>([]);
  const [segments, setSegments] = useState<TrackSegment[]>([]);
  const [imageMetadata, setImageMetadata] = useState<ImageMetadata | null>(null);

  // Segment mode selection and search
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number | null>(null);
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);
  const [segmentSearchQuery, setSegmentSearchQuery] = useState('');

  // Segment width tool state
  const [segmentToolOpen, setSegmentToolOpen] = useState(false);
  const [segFrom, setSegFrom] = useState(1);
  const [segTo, setSegTo] = useState(3);
  const [segStartWidth, setSegStartWidth] = useState(8.0);
  const [segEndWidth, setSegEndWidth] = useState(8.0);
  const [segMode, setSegMode] = useState<'gradient' | 'constant'>('constant');

  // Selection
  const [selectedPointIndices, setSelectedPointIndices] = useState<number[]>([]);
  const selectedSet = React.useMemo(() => new Set(selectedPointIndices), [selectedPointIndices]);
  const selectedPointIndex = selectedPointIndices.length === 1 ? selectedPointIndices[0] : null;
  const [marqueeBox, setMarqueeBox] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [selectedHandle, setSelectedHandle] = useState<'corner' | 'entry' | 'apex' | 'exit' | 'marker' | null>(null);
  const [contextMenu, setContextMenu] = useState<TrackContextMenu | null>(null);

  // Layer toggles
  const [showSatellite, setShowSatellite] = useState(true);
  const [showCenterline, setShowCenterline] = useState(true);
  const [showBoundaries, setShowBoundaries] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);

  // History stack for Undo/Redo
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  // Saved state snapshot to track unsaved changes
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<{
    controlPoints: TrackPoint[];
    trackWidthM: number;
    annotations: TrackAnnotation[];
    segments: TrackSegment[];
  } | null>(null);

  const hasChanges = React.useMemo(() => {
    if (!lastSavedSnapshot) return false;
    if (controlPoints.length !== lastSavedSnapshot.controlPoints.length) return true;
    if (Math.abs(trackWidthM - lastSavedSnapshot.trackWidthM) > 0.01) return true;

    // Check control points
    for (let i = 0; i < controlPoints.length; i++) {
      const a = controlPoints[i];
      const b = lastSavedSnapshot.controlPoints[i];
      if (a.lat !== b.lat || a.lng !== b.lng || a.widthM !== b.widthM) return true;
    }

    // Check annotations
    if (annotations.length !== lastSavedSnapshot.annotations.length) return true;
    for (let i = 0; i < annotations.length; i++) {
      const a = annotations[i];
      const b = lastSavedSnapshot.annotations[i];
      if (a.id !== b.id || a.type !== b.type || a.name !== b.name || a.direction !== b.direction) return true;
      if (a.location?.lat !== b.location?.lat || a.location?.lng !== b.location?.lng) return true;
      if (a.entry?.lat !== b.entry?.lat || a.entry?.lng !== b.entry?.lng) return true;
      if (a.apex?.lat !== b.apex?.lat || a.apex?.lng !== b.apex?.lng) return true;
      if (a.exit?.lat !== b.exit?.lat || a.exit?.lng !== b.exit?.lng) return true;
    }

    // Check segments
    if (segments.length !== (lastSavedSnapshot.segments?.length || 0)) return true;
    for (let i = 0; i < segments.length; i++) {
      const a = segments[i];
      const b = lastSavedSnapshot.segments[i];
      if (!b || a.index !== b.index || a.lat !== b.lat || a.lng !== b.lng) return true;
    }

    return false;
  }, [lastSavedSnapshot, controlPoints, trackWidthM, annotations, segments]);

  // Viewport & Pan/Zoom
  const [viewport, setViewport] = useState({
    centerLat: latitude || 47.0,
    centerLng: longitude || 8.0,
    scale: 120000, // Pixels per degree
    offsetX: 0,
    offsetY: 0,
  });
  const [altPressed, setAltPressed] = useState(false);

  // Satellite image element
  const [satelliteImage, setSatelliteImage] = useState<HTMLImageElement | null>(null);
  const tileCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const [tileRenderTrigger, setTileRenderTrigger] = useState(0);
  const requestTileRedraw = useCallback(() => {
    setTileRenderTrigger(c => (c + 1) % 10000);
  }, []);

  // Mouse interaction state
  const isDraggingRef = useRef(false);
  const dragTargetRef = useRef<{
    type: 'point' | 'points' | 'marquee' | 'annotation-whole' | 'annotation-handle' | 'pan';
    pointIndex?: number;
    dragPointIndex?: number;
    initialPositions?: Array<{ idx: number; lat: number; lng: number }>;
    isShift?: boolean;
    initialSelected?: number[];
    annotId?: string;
    handleType?: 'entry' | 'apex' | 'exit' | 'marker';
    startX: number;
    startY: number;
    startLat?: number;
    startLng?: number;
    startPositions?: { entryT: number; apexT: number; exitT: number; startMouseT: number };
  } | null>(null);

  // Push snapshot to history
  const pushState = useCallback((newPoints: TrackPoint[], newWidth: number, newAnnots: TrackAnnotation[], newSegments?: TrackSegment[]) => {
    const { inner, outer, totalLengthM } = computeRibbonBoundaries(newPoints, newWidth);
    const segsToSave = newSegments !== undefined ? newSegments : segments;
    const newShape: TrackShape = {
      trackId,
      centerline: newPoints,
      trackWidthM: newWidth,
      innerBoundary: inner,
      outerBoundary: outer,
      totalLengthM,
      segments: segsToSave,
    };

    setHistory(prev => {
      const trimmed = prev.slice(0, historyIndex + 1);
      return [...trimmed, { shape: newShape, annotations: newAnnots }];
    });
    setHistoryIndex(prev => prev + 1);
  }, [trackId, historyIndex, segments]);

  // Initial load
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoading(true);
      try {
        const resp = await rpc<{
          shape: TrackShape | null;
          annotations: TrackAnnotation[];
          imageMetadata?: ImageMetadata;
        }>('GetTrackShapeAndAnnotations', { trackId });

        if (!isMounted) return;

        if (resp.imageMetadata) {
          setImageMetadata(resp.imageMetadata);
        }

        let initialPoints: TrackPoint[] = [];
        let initialWidth = 8.0;
        let initialSegments: TrackSegment[] = [];

        if (resp.shape && resp.shape.centerline && resp.shape.centerline.length > 0) {
          initialPoints = resp.shape.centerline;
          initialWidth = resp.shape.trackWidthM || 8.0;
          initialSegments = resp.shape.segments || [];
        }

        const defaultPt = initialPoints[0] || { lat: latitude || 47.0, lng: longitude || 8.0 };
        const samples = sampleClosedSpline(initialPoints, initialWidth, 20);
        let initialAnnots: TrackAnnotation[] = ensureFinishLine(resp.annotations || [], trackId, defaultPt);
        initialAnnots = renumberCornersByFinishLine(initialAnnots, samples);

        setControlPoints(initialPoints);
        setTrackWidthM(initialWidth);
        setAnnotations(initialAnnots);
        setSegments(initialSegments);
        setLastSavedSnapshot({
          controlPoints: JSON.parse(JSON.stringify(initialPoints)),
          trackWidthM: initialWidth,
          annotations: JSON.parse(JSON.stringify(initialAnnots)),
          segments: JSON.parse(JSON.stringify(initialSegments)),
        });

        const { inner, outer, totalLengthM } = computeRibbonBoundaries(initialPoints, initialWidth);
        const initShape: TrackShape = {
          trackId,
          centerline: initialPoints,
          trackWidthM: initialWidth,
          innerBoundary: inner,
          outerBoundary: outer,
          totalLengthM,
          segments: initialSegments,
        };

        setHistory([{ shape: initShape, annotations: initialAnnots }]);
        setHistoryIndex(0);

        // Center and scale viewport to comfortably fit track or satellite image
        if (initialPoints.length > 0) {
          let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
          for (const p of initialPoints) {
            if (p.lat < minLat) minLat = p.lat;
            if (p.lat > maxLat) maxLat = p.lat;
            if (p.lng < minLng) minLng = p.lng;
            if (p.lng > maxLng) maxLng = p.lng;
          }
          const cLat = (minLat + maxLat) / 2;
          const cLng = (minLng + maxLng) / 2;
          const dLat = Math.max(0.0008, (maxLat - minLat) * 1.4);
          const dLng = Math.max(0.0008, (maxLng - minLng) * 1.4);
          const cosLat = Math.cos((cLat * Math.PI) / 180);
          const targetScale = Math.min(
            (window.innerWidth * 0.55) / (dLng * cosLat),
            (window.innerHeight * 0.55) / dLat
          );
          setViewport(v => ({
            ...v,
            centerLat: cLat,
            centerLng: cLng,
            scale: Math.max(30000, Math.min(600000, targetScale)),
            offsetX: 0,
            offsetY: 0,
          }));
        } else if (resp.imageMetadata) {
          const m = resp.imageMetadata;
          const dLat = Math.max(0.0008, (m.maxLat - m.minLat) * 1.2);
          const dLng = Math.max(0.0008, (m.maxLng - m.minLng) * 1.2);
          const cosLat = Math.cos((m.centerLat * Math.PI) / 180);
          const targetScale = Math.min(
            (window.innerWidth * 0.55) / (dLng * cosLat),
            (window.innerHeight * 0.55) / dLat
          );
          setViewport(v => ({
            ...v,
            centerLat: m.centerLat,
            centerLng: m.centerLng,
            scale: Math.max(30000, Math.min(600000, targetScale)),
            offsetX: 0,
            offsetY: 0,
          }));
        }
      } catch (e) {
        showToast(t('trackStudio.saveError', { error: (e as Error).message }), 'error');
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadData();

    // Load satellite image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `/rpc/GetTrackImage?trackId=${encodeURIComponent(trackId)}&v=${Date.now()}`;
    img.onload = () => {
      if (isMounted) setSatelliteImage(img);
    };

    return () => {
      isMounted = false;
    };
  }, [trackId]);

  // Sync segment tool default indices when point selection changes
  useEffect(() => {
    if (selectedPointIndex !== null && controlPoints.length > 0) {
      setSegFrom(selectedPointIndex + 1);
      const nextIdx = ((selectedPointIndex + 2) % controlPoints.length) + 1;
      setSegTo(nextIdx);
      const currentPointWidth = controlPoints[selectedPointIndex].widthM ?? trackWidthM;
      setSegStartWidth(currentPointWidth);
      setSegEndWidth(currentPointWidth);
    }
  }, [selectedPointIndex, controlPoints.length, trackWidthM]);

  // Undo / Redo handlers
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIdx = historyIndex - 1;
      const snap = history[newIdx];
      setControlPoints(snap.shape.centerline);
      setTrackWidthM(snap.shape.trackWidthM);
      setAnnotations(snap.annotations);
      setHistoryIndex(newIdx);
    }
  }, [historyIndex, history]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIdx = historyIndex + 1;
      const snap = history[newIdx];
      setControlPoints(snap.shape.centerline);
      setTrackWidthM(snap.shape.trackWidthM);
      setAnnotations(snap.annotations);
      setHistoryIndex(newIdx);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts (Cmd+Z, Cmd+A, Delete, Escape) & Alt key tracking
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        setAltPressed(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
          return;
        }
        if (mode === 'spline') {
          e.preventDefault();
          setSelectedPointIndices(controlPoints.map((_, i) => i));
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
          return;
        }
        if (mode === 'spline' && selectedPointIndices.length > 0 && controlPoints.length - selectedPointIndices.length >= 3) {
          e.preventDefault();
          deleteSelectedPoints();
        } else if ((mode === 'corners' || mode === 'sectors') && selectedAnnotationId !== null) {
          e.preventDefault();
          deleteSelectedAnnotation();
        }
      } else if (e.key === 'Escape') {
        if (contextMenu) {
          setContextMenu(null);
          return;
        }
        setSelectedPointIndices([]);
        setSelectedAnnotationId(null);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        setAltPressed(false);
      }
    };

    const handleBlur = () => {
      setAltPressed(false);
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [handleUndo, handleRedo, mode, selectedPointIndices, selectedAnnotationId, controlPoints, contextMenu]);

  // Coordinate transforms
  const gpsToCanvas = useCallback(
    (lat: number, lng: number, width: number, height: number): { x: number; y: number } => {
      const cosLat = Math.cos((viewport.centerLat * Math.PI) / 180);
      const x = width / 2 + (lng - viewport.centerLng) * viewport.scale * cosLat + viewport.offsetX;
      const y = height / 2 - (lat - viewport.centerLat) * viewport.scale + viewport.offsetY;
      return { x, y };
    },
    [viewport]
  );

  const canvasToGPS = useCallback(
    (x: number, y: number, width: number, height: number): TrackPoint => {
      const cosLat = Math.cos((viewport.centerLat * Math.PI) / 180);
      const lng = viewport.centerLng + (x - width / 2 - viewport.offsetX) / (viewport.scale * cosLat);
      const lat = viewport.centerLat - (y - height / 2 - viewport.offsetY) / viewport.scale;
      return { lat, lng };
    },
    [viewport]
  );

  const renderDynamicGoogleTiles = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      const nw = canvasToGPS(0, 0, width, height);
      const se = canvasToGPS(width, height, width, height);

      const minLat = Math.min(nw.lat, se.lat);
      const maxLat = Math.max(nw.lat, se.lat);
      const minLng = Math.min(nw.lng, se.lng);
      const maxLng = Math.max(nw.lng, se.lng);

      const metersPerPixel = 111195 / Math.max(1, viewport.scale);
      const latRad = (viewport.centerLat * Math.PI) / 180;
      const earthCircumferenceAtLat = 40075016.686 * Math.cos(latRad);
      const idealZoom = Math.log2(earthCircumferenceAtLat / (256 * metersPerPixel));
      const zoomLevel = Math.max(12, Math.min(20, Math.round(idealZoom)));

      const tileMin = getTileCoords(maxLat, minLng, zoomLevel);
      const maxTile = getTileCoords(minLat, maxLng, zoomLevel);

      const startX = Math.min(tileMin.x, maxTile.x) - 1;
      const endX = Math.max(tileMin.x, maxTile.x) + 1;
      const startY = Math.min(tileMin.y, maxTile.y) - 1;
      const endY = Math.max(tileMin.y, maxTile.y) + 1;

      const totalTiles = (endX - startX + 1) * (endY - startY + 1);
      if (totalTiles <= 80) {
        for (let tileX = startX; tileX <= endX; tileX++) {
          for (let tileY = startY; tileY <= endY; tileY++) {
            const tileKey = `GOOGLE_${zoomLevel}_${tileX}_${tileY}`;

            const tileNwLat = tileToLat(tileY, zoomLevel);
            const tileNwLng = tileToLng(tileX, zoomLevel);
            const tileSeLat = tileToLat(tileY + 1, zoomLevel);
            const tileSeLng = tileToLng(tileX + 1, zoomLevel);

            const cNW = gpsToCanvas(tileNwLat, tileNwLng, width, height);
            const cSE = gpsToCanvas(tileSeLat, tileSeLng, width, height);

            const tileWidth = cSE.x - cNW.x;
            const tileHeight = cSE.y - cNW.y;

            let img = tileCacheRef.current.get(tileKey);
            if (!img) {
              img = new Image();
              img.crossOrigin = 'anonymous';
              img.src = `https://mt1.google.com/vt/lyrs=s&x=${tileX}&y=${tileY}&z=${zoomLevel}`;
              img.onload = () => requestTileRedraw();
              tileCacheRef.current.set(tileKey, img);
            } else if (img.complete && img.naturalWidth > 0) {
              ctx.drawImage(img, cNW.x, cNW.y, tileWidth, tileHeight);
            }
          }
        }
      }
    },
    [canvasToGPS, gpsToCanvas, viewport, requestTileRedraw]
  );

  // Spline samples & calculations
  const splineSamples = React.useMemo(() => {
    return sampleClosedSpline(controlPoints, trackWidthM, 30);
  }, [controlPoints, trackWidthM]);

  const { inner: innerBounds, outer: outerBounds, totalLengthM } = React.useMemo(() => {
    return computeRibbonBoundaries(controlPoints, trackWidthM, 25);
  }, [controlPoints, trackWidthM]);

  // Spline editing helpers
  const deleteSelectedPoints = () => {
    if (selectedPointIndices.length === 0) return;
    if (controlPoints.length - selectedPointIndices.length < 3) {
      showToast('Track shape must have at least 3 control points.', 'error');
      return;
    }
    const toRemove = new Set(selectedPointIndices);
    const updated = controlPoints.filter((_, i) => !toRemove.has(i));
    setControlPoints(updated);
    setSelectedPointIndices([]);
    pushState(updated, trackWidthM, annotations);
  };

  const deleteSelectedPoint = () => {
    deleteSelectedPoints();
  };

  const evenlySpacePoints = () => {
    if (splineSamples.length < 10 || controlPoints.length < 4) return;
    const count = controlPoints.length;
    const resampled: TrackPoint[] = [];
    for (let i = 0; i < count; i++) {
      const t = i / count;
      resampled.push(getPointAtT(t, controlPoints));
    }
    setControlPoints(resampled);
    pushState(resampled, trackWidthM, annotations);
  };

  const reverseDirection = () => {
    if (controlPoints.length < 3) return;
    const reversed = [...controlPoints].reverse();

    // Preserve physical locations on asphalt, swap entry & exit for corners
    const updatedAnnotations = annotations.map(a => {
      if (a.type === 'corner') {
        const newDirection = a.direction === 'left' ? 'right' : a.direction === 'right' ? 'left' : undefined;
        return {
          ...a,
          entry: a.exit,
          exit: a.entry,
          direction: newDirection,
        };
      }
      return a;
    });

    const reversedSamples = sampleClosedSpline(reversed, trackWidthM, 20);
    const renumbered = renumberCornersByFinishLine(updatedAnnotations, reversedSamples);
    setControlPoints(reversed);
    setAnnotations(renumbered);
    pushState(reversed, trackWidthM, renumbered);
  };

  const handleUpdatePointWidth = (width: number | undefined) => {
    if (selectedPointIndices.length === 0) return;
    const targetIndices = new Set(selectedPointIndices);
    const updated = controlPoints.map((p, idx) => {
      if (!targetIndices.has(idx)) return p;
      if (width === undefined || width <= 0) {
        const cp = { ...p };
        delete cp.widthM;
        return cp;
      }
      return {
        ...p,
        widthM: Math.round(width * 10) / 10,
      };
    });
    setControlPoints(updated);
    pushState(updated, trackWidthM, annotations);
  };

  const handleApplySegmentWidth = () => {
    if (controlPoints.length < 3) return;
    const startIdx = segFrom - 1;
    const endIdx = segTo - 1;
    const endW = segMode === 'constant' ? segStartWidth : segEndWidth;
    const updated = applyWidthToRange(controlPoints, startIdx, endIdx, segStartWidth, endW);
    setControlPoints(updated);
    pushState(updated, trackWidthM, annotations);
    showToast(t('trackStudio.segmentApplied', { from: String(segFrom), to: String(segTo) }), 'success');
  };

  const handleResetSegmentWidth = () => {
    if (controlPoints.length < 3) return;
    const startIdx = segFrom - 1;
    const endIdx = segTo - 1;
    const updated = resetWidthInRange(controlPoints, startIdx, endIdx);
    setControlPoints(updated);
    pushState(updated, trackWidthM, annotations);
    showToast(t('trackStudio.segmentApplied', { from: String(segFrom), to: String(segTo) }), 'success');
  };

  // Annotation helpers (always placed on the middle spline line as exact geo locations)
  const addCornerAtT = (apexT: number) => {
    const circuitLength = totalLengthM > 0 ? totalLengthM : 1000;
    const spanM = 18.0; // 18m entry and 18m exit
    const deltaT = spanM / circuitLength;
    const entryT = ((apexT - deltaT) % 1.0 + 1.0) % 1.0;
    const exitT = ((apexT + deltaT) % 1.0 + 1.0) % 1.0;

    const entryPt = getPointAtT(entryT, controlPoints);
    const apexPt = getPointAtT(apexT, controlPoints);
    const exitPt = getPointAtT(exitT, controlPoints);
    const dir = inferTurnDirectionAtT(apexT, splineSamples);

    const newAnnot: TrackAnnotation = {
      id: `${trackId}-corner-${Date.now()}`,
      trackId,
      type: 'corner',
      name: 'Turn',
      orderIndex: annotations.length,
      entry: entryPt,
      apex: apexPt,
      exit: exitPt,
      direction: dir,
    };

    const updated = renumberCornersByFinishLine([...annotations, newAnnot], splineSamples);
    setAnnotations(updated);
    setSelectedAnnotationId(newAnnot.id!);
    setSelectedHandle('corner');
    pushState(controlPoints, trackWidthM, updated);
  };

  const addSectorAtT = (t: number) => {
    const existingSectors = annotations.filter(a => a.type === 'sector').length;
    const nextNum = existingSectors + 1;
    const pt = getPointAtT(t, controlPoints);

    const newAnnot: TrackAnnotation = {
      id: `${trackId}-sector-${Date.now()}`,
      trackId,
      type: 'sector',
      name: `Sector ${nextNum}`,
      orderIndex: annotations.length,
      location: pt,
    };

    const updated = [...annotations, newAnnot];
    setAnnotations(updated);
    setSelectedAnnotationId(newAnnot.id!);
    setSelectedHandle('marker');
    pushState(controlPoints, trackWidthM, updated);
  };

  const setFinishLineAtT = (t: number) => {
    const pt = getPointAtT(t, controlPoints);
    const existing = annotations.find(a => a.type === 'finish_line');
    let updated: TrackAnnotation[];
    if (existing) {
      updated = annotations.map(a =>
        a.id === existing.id ? { ...a, location: pt } : a
      );
    } else {
      const newFinish: TrackAnnotation = {
        id: `${trackId}-finish-line`,
        trackId,
        type: 'finish_line',
        name: 'Start / Finish Line',
        orderIndex: 0,
        location: pt,
      };
      updated = [newFinish, ...annotations];
    }

    const renumbered = renumberCornersByFinishLine(updated, splineSamples);
    setAnnotations(renumbered);
    const finishId = existing?.id || `${trackId}-finish-line`;
    setSelectedAnnotationId(finishId);
    setSelectedHandle('marker');
    pushState(controlPoints, trackWidthM, renumbered);
  };

  const deleteSelectedAnnotation = () => {
    if (!selectedAnnotationId) return;
    const target = annotations.find(a => a.id === selectedAnnotationId);
    if (target?.type === 'finish_line') {
      showToast('Start / Finish Line cannot be deleted.', 'error');
      return;
    }
    const updated = renumberCornersByFinishLine(
      annotations.filter(a => a.id !== selectedAnnotationId),
      splineSamples
    );
    setAnnotations(updated);
    setSelectedAnnotationId(null);
    pushState(controlPoints, trackWidthM, updated);
  };

  const updateSelectedAnnotation = (updates: Partial<TrackAnnotation>) => {
    if (!selectedAnnotationId) return;
    const target = annotations.find(a => a.id === selectedAnnotationId);
    let updated = annotations.map(a => (a.id === selectedAnnotationId ? { ...a, ...updates } : a));
    if (updates.apex !== undefined || target?.type === 'finish_line') {
      updated = renumberCornersByFinishLine(updated, splineSamples);
    }
    setAnnotations(updated);
    pushState(controlPoints, trackWidthM, updated);
  };

  // Bake 200 static segments
  const handleBakeSegments = () => {
    if (controlPoints.length < 3) {
      showToast(t('trackStudio.noCenterline'), 'error');
      return;
    }
    const finishLine = annotations.find(a => a.type === 'finish_line');
    const baked = bakeTrackSegments(controlPoints, 200, finishLine?.location);
    setSegments(baked);
    pushState(controlPoints, trackWidthM, annotations, baked);
    showToast(t('trackStudio.segmentsBakedSuccess', { count: String(baked.length) }), 'success');
  };

  // Save changes to backend
  const handleSave = useCallback(async (isAutoSave = false) => {
    if (controlPoints.length < 3) {
      if (!isAutoSave) {
        showToast(t('trackStudio.noCenterline'), 'error');
      }
      return;
    }

    setSaving(true);
    try {
      const shapePayload: TrackShape = {
        trackId,
        centerline: controlPoints,
        trackWidthM,
        innerBoundary: innerBounds,
        outerBoundary: outerBounds,
        totalLengthM,
        segments,
      };

      const normalizedAnnotations = renumberCornersByFinishLine(annotations, splineSamples);
      setAnnotations(normalizedAnnotations);

      await rpc('SaveTrackShapeAndAnnotations', {
        trackId,
        shape: shapePayload,
        annotations: normalizedAnnotations,
      });

      if (isAutoSave) {
        showToast(t('trackStudio.autoSaveSuccess'));
      } else {
        showToast(t('trackStudio.saveSuccess'));
      }
      setLastSavedSnapshot({
        controlPoints: JSON.parse(JSON.stringify(controlPoints)),
        trackWidthM,
        annotations: JSON.parse(JSON.stringify(normalizedAnnotations)),
        segments: JSON.parse(JSON.stringify(segments)),
      });
      onSaved?.();
    } catch (e) {
      showToast(t('trackStudio.saveError', { error: (e as Error).message }), 'error');
    } finally {
      setSaving(false);
    }
  }, [
    trackId,
    controlPoints,
    trackWidthM,
    innerBounds,
    outerBounds,
    totalLengthM,
    segments,
    annotations,
    showToast,
    t,
    onSaved,
  ]);

  // Periodic auto save: 10s after completing an action if there are changes
  useEffect(() => {
    if (loading || saving || !hasChanges || isDragging || controlPoints.length < 3) {
      return;
    }

    const timer = setTimeout(() => {
      handleSave(true);
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [hasChanges, isDragging, loading, saving, controlPoints.length, handleSave]);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Clear background
    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, width, height);

    // 1. Draw satellite imagery if enabled
    if (showSatellite) {
      ctx.save();
      ctx.globalAlpha = 0.85;

      // Fallback base composite image (if available)
      if (satelliteImage) {
        const imgBounds = imageMetadata || computeImageGeoBounds(
          latitude || viewport.centerLat,
          longitude || viewport.centerLng,
          zoom || 18,
          800,
          800
        );
        const topLeft = gpsToCanvas(imgBounds.maxLat, imgBounds.minLng, width, height);
        const bottomRight = gpsToCanvas(imgBounds.minLat, imgBounds.maxLng, width, height);
        ctx.drawImage(
          satelliteImage,
          topLeft.x,
          topLeft.y,
          bottomRight.x - topLeft.x,
          bottomRight.y - topLeft.y
        );
      }

      // Draw dynamic Google Satellite tiles (exact same tiles and provider as graphs/tools)
      renderDynamicGoogleTiles(ctx, width, height);

      ctx.restore();
    }

    // Grid lines for reference
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
    ctx.lineWidth = 1;
    const step = 50;
    for (let x = 0; x < width; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // 2. Draw track ribbon asphalt fill & boundaries
    if (showBoundaries && innerBounds.length > 2 && outerBounds.length > 2) {
      // Asphalt track surface polygon
      ctx.beginPath();
      const firstInner = gpsToCanvas(innerBounds[0].lat, innerBounds[0].lng, width, height);
      ctx.moveTo(firstInner.x, firstInner.y);
      for (let i = 1; i < innerBounds.length; i++) {
        const pt = gpsToCanvas(innerBounds[i].lat, innerBounds[i].lng, width, height);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.closePath();

      for (let i = outerBounds.length - 1; i >= 0; i--) {
        const pt = gpsToCanvas(outerBounds[i].lat, outerBounds[i].lng, width, height);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(124, 92, 252, 0.12)';
      ctx.fill();

      // Outer boundary line
      ctx.beginPath();
      const firstOuter = gpsToCanvas(outerBounds[0].lat, outerBounds[0].lng, width, height);
      ctx.moveTo(firstOuter.x, firstOuter.y);
      for (let i = 1; i < outerBounds.length; i++) {
        const pt = gpsToCanvas(outerBounds[i].lat, outerBounds[i].lng, width, height);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(168, 85, 247, 0.6)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Inner boundary line
      ctx.beginPath();
      ctx.moveTo(firstInner.x, firstInner.y);
      for (let i = 1; i < innerBounds.length; i++) {
        const pt = gpsToCanvas(innerBounds[i].lat, innerBounds[i].lng, width, height);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.6)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // 3. Draw Centerline spline
    if (showCenterline && splineSamples.length > 2) {
      ctx.beginPath();
      const firstSample = gpsToCanvas(splineSamples[0].point.lat, splineSamples[0].point.lng, width, height);
      ctx.moveTo(firstSample.x, firstSample.y);
      for (let i = 1; i < splineSamples.length; i++) {
        const pt = gpsToCanvas(splineSamples[i].point.lat, splineSamples[i].point.lng, width, height);
        ctx.lineTo(pt.x, pt.y);
      }
      ctx.closePath();
      ctx.strokeStyle = 'rgba(250, 204, 21, 0.85)';
      ctx.lineWidth = 3;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 4. Draw Corner Spans & Annotations
    if (showAnnotations) {
      const orderedAnnotations = renumberCornersByFinishLine(annotations, splineSamples);
      for (const a of orderedAnnotations) {
        const isSelected = a.id === selectedAnnotationId;

        if (a.type === 'corner') {
          // Only render corners in Corners mode
          if (mode !== 'corners') continue;

          const entryPt = a.entry || getPointAtT(0, controlPoints);
          const apexPt = a.apex || getPointAtT(0, controlPoints);
          const exitPt = a.exit || getPointAtT(0, controlPoints);

          const cEntry = gpsToCanvas(entryPt.lat, entryPt.lng, width, height);
          const cApex = gpsToCanvas(apexPt.lat, apexPt.lng, width, height);
          const cExit = gpsToCanvas(exitPt.lat, exitPt.lng, width, height);

          // Highlight corner span on track
          ctx.beginPath();
          ctx.arc(cApex.x, cApex.y, 22, 0, Math.PI * 2);
          ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.12)';
          ctx.fill();

          // Apex Badge
          ctx.beginPath();
          ctx.arc(cApex.x, cApex.y, 14, 0, Math.PI * 2);
          ctx.fillStyle = isSelected ? '#ef4444' : '#b91c1c';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = isSelected ? 2.5 : 1.5;
          ctx.stroke();

          // Corner text (dynamic T1..TN)
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 10px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`T${a.orderIndex || 1}`, cApex.x, cApex.y);

          // Entry & Exit handle lines and dots when selected
          if (isSelected || mode === 'corners') {
            // Line from entry to apex to exit
            ctx.beginPath();
            ctx.moveTo(cEntry.x, cEntry.y);
            ctx.lineTo(cApex.x, cApex.y);
            ctx.lineTo(cExit.x, cExit.y);
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Entry Handle (Braking / Turn-in)
            ctx.beginPath();
            ctx.arc(cEntry.x, cEntry.y, 6, 0, Math.PI * 2);
            ctx.fillStyle = '#f97316'; // orange
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Exit Handle (Track-out / Acceleration)
            ctx.beginPath();
            ctx.arc(cExit.x, cExit.y, 6, 0, Math.PI * 2);
            ctx.fillStyle = '#22c55e'; // green
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        } else if (a.type === 'finish_line') {
          // Only render start & finish line in Sectors mode
          if (mode !== 'sectors') continue;

          const pt = a.location || getPointAtT(0, controlPoints);
          const cPt = gpsToCanvas(pt.lat, pt.lng, width, height);

          // Draw perpendicular checkered bar across track asphalt
          if (splineSamples.length > 2) {
            const sampleInfo = findClosestParametricT(pt, splineSamples);
            const sample = splineSamples[sampleInfo.index];
            const norm = sampleInfo.normal || sample?.normal || { x: 0, y: 1 };
            const localWidth = sample?.widthM ?? trackWidthM ?? 8.0;
            const halfW = localWidth / 2.0;
            const latRad = (pt.lat * Math.PI) / 180;
            const cosLat = Math.cos(latRad);
            const dLat = ((norm.y * halfW) / 6371000.0) * (180 / Math.PI);
            const dLon = ((norm.x * halfW) / (6371000.0 * cosLat)) * (180 / Math.PI);

            const innerPt = gpsToCanvas(pt.lat + dLat, pt.lng + dLon, width, height);
            const outerPt = gpsToCanvas(pt.lat - dLat, pt.lng - dLon, width, height);

            // Bold white base line across track ribbon
            ctx.beginPath();
            ctx.moveTo(innerPt.x, innerPt.y);
            ctx.lineTo(outerPt.x, outerPt.y);
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 4;
            ctx.stroke();

            // Black dashed overlay for checkered pattern
            ctx.beginPath();
            ctx.moveTo(innerPt.x, innerPt.y);
            ctx.lineTo(outerPt.x, outerPt.y);
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 4;
            ctx.setLineDash([5, 5]);
            ctx.stroke();
            ctx.setLineDash([]);

            // Forward driving direction arrow across the track ribbon
            const tx = norm.y; // unit tangent East component (m)
            const ty = -norm.x; // unit tangent North component (m)
            const arrowBaseDistM = 5.0;
            const arrowTipDistM = 11.0;
            const arrowWingDistM = 3.0;

            const arrowBaseLat = pt.lat + ((ty * arrowBaseDistM) / 6371000.0) * (180 / Math.PI);
            const arrowBaseLng = pt.lng + ((tx * arrowBaseDistM) / (6371000.0 * cosLat)) * (180 / Math.PI);

            const arrowTipLat = pt.lat + ((ty * arrowTipDistM) / 6371000.0) * (180 / Math.PI);
            const arrowTipLng = pt.lng + ((tx * arrowTipDistM) / (6371000.0 * cosLat)) * (180 / Math.PI);

            const wingLeftLat = arrowBaseLat + ((norm.y * arrowWingDistM) / 6371000.0) * (180 / Math.PI);
            const wingLeftLng = arrowBaseLng + ((norm.x * arrowWingDistM) / (6371000.0 * cosLat)) * (180 / Math.PI);

            const wingRightLat = arrowBaseLat - ((norm.y * arrowWingDistM) / 6371000.0) * (180 / Math.PI);
            const wingRightLng = arrowBaseLng - ((norm.x * arrowWingDistM) / (6371000.0 * cosLat)) * (180 / Math.PI);

            const cTip = gpsToCanvas(arrowTipLat, arrowTipLng, width, height);
            const cLeft = gpsToCanvas(wingLeftLat, wingLeftLng, width, height);
            const cRight = gpsToCanvas(wingRightLat, wingRightLng, width, height);

            ctx.beginPath();
            ctx.moveTo(cTip.x, cTip.y);
            ctx.lineTo(cLeft.x, cLeft.y);
            ctx.lineTo(cRight.x, cRight.y);
            ctx.closePath();
            ctx.fillStyle = '#22c55e';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }

          // Flag circle badge
          ctx.beginPath();
          ctx.arc(cPt.x, cPt.y, isSelected ? 15 : 13, 0, Math.PI * 2);
          ctx.fillStyle = isSelected ? '#2563eb' : '#1e293b';
          ctx.fill();
          ctx.strokeStyle = isSelected ? '#60a5fa' : '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();

          if (isSelected) {
            ctx.beginPath();
            ctx.arc(cPt.x, cPt.y, 22, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(59, 130, 246, 0.45)';
            ctx.lineWidth = 2;
            ctx.stroke();
          }

          ctx.fillStyle = '#ffffff';
          ctx.font = '12px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('🏁', cPt.x, cPt.y);
        } else if (a.type === 'sector') {
          // Only render sectors in Sectors mode
          if (mode !== 'sectors') continue;

          const pt = a.location || getPointAtT(0, controlPoints);
          const cPt = gpsToCanvas(pt.lat, pt.lng, width, height);

          ctx.beginPath();
          ctx.arc(cPt.x, cPt.y, 10, 0, Math.PI * 2);
          ctx.fillStyle = isSelected ? '#8b5cf6' : '#6d28d9';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 9px Inter, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(a.name.replace('Sector ', 'S').replace('Sector', 'S'), cPt.x, cPt.y);
        }
      }
    }

    // 5. Draw Control Points in Spline Mode
    if (mode === 'spline') {
      // Draw selection marquee if active
      if (marqueeBox) {
        const w = marqueeBox.x2 - marqueeBox.x1;
        const h = marqueeBox.y2 - marqueeBox.y1;
        ctx.fillStyle = 'rgba(124, 92, 252, 0.15)';
        ctx.fillRect(marqueeBox.x1, marqueeBox.y1, w, h);
        ctx.strokeStyle = '#7c5cfc';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(marqueeBox.x1, marqueeBox.y1, w, h);
        ctx.setLineDash([]);
      }

      controlPoints.forEach((p, idx) => {
        const c = gpsToCanvas(p.lat, p.lng, width, height);
        const isSelected = selectedSet.has(idx);
        const hasCustomWidth = p.widthM !== undefined && p.widthM > 0 && Math.abs(p.widthM - trackWidthM) > 0.05;
        const localWidth = p.widthM !== undefined && p.widthM > 0 ? p.widthM : trackWidthM;

        // If point is selected, draw normal whiskers showing exact ribbon boundary width on asphalt
        if (isSelected && selectedPointIndices.length <= 12 && splineSamples.length > 2) {
          const sampleInfo = findClosestParametricT(p, splineSamples);
          const norm = sampleInfo.normal || splineSamples[sampleInfo.index]?.normal || { x: 0, y: 1 };
          const halfW = localWidth / 2.0;
          const latRad = (p.lat * Math.PI) / 180;
          const cosLat = Math.cos(latRad);
          const dLat = ((norm.y * halfW) / 6371000.0) * (180 / Math.PI);
          const dLon = ((norm.x * halfW) / (6371000.0 * cosLat)) * (180 / Math.PI);

          const innerWhisker = gpsToCanvas(p.lat + dLat, p.lng + dLon, width, height);
          const outerWhisker = gpsToCanvas(p.lat - dLat, p.lng - dLon, width, height);

          // Draw perpendicular width indicator line
          ctx.beginPath();
          ctx.moveTo(innerWhisker.x, innerWhisker.y);
          ctx.lineTo(outerWhisker.x, outerWhisker.y);
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.stroke();
          ctx.setLineDash([]);

          // Boundary crossbars
          ctx.beginPath();
          ctx.arc(innerWhisker.x, innerWhisker.y, 4, 0, Math.PI * 2);
          ctx.arc(outerWhisker.x, outerWhisker.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#38bdf8';
          ctx.fill();

          // Width badge (only when single point or a few points selected to avoid clutter)
          if (selectedPointIndices.length <= 3) {
            const midX = (innerWhisker.x + outerWhisker.x) / 2;
            const midY = (innerWhisker.y + outerWhisker.y) / 2 - 18;
            ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
            ctx.fillRect(midX - 26, midY - 9, 52, 18);
            ctx.strokeStyle = '#38bdf8';
            ctx.lineWidth = 1;
            ctx.strokeRect(midX - 26, midY - 9, 52, 18);
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 10px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${localWidth.toFixed(1)}m`, midX, midY);
          }
        }

        ctx.beginPath();
        ctx.arc(c.x, c.y, isSelected ? 8 : (hasCustomWidth ? 6 : 5), 0, Math.PI * 2);
        ctx.fillStyle = isSelected ? '#7c5cfc' : (hasCustomWidth ? '#38bdf8' : '#ffffff');
        ctx.fill();
        ctx.strokeStyle = isSelected ? '#ffffff' : '#000000';
        ctx.lineWidth = isSelected ? 2 : 1.5;
        ctx.stroke();

        if (isSelected) {
          ctx.beginPath();
          ctx.arc(c.x, c.y, 13, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(124, 92, 252, 0.6)';
          ctx.lineWidth = 2;
          ctx.stroke();
        } else if (hasCustomWidth) {
          ctx.beginPath();
          ctx.arc(c.x, c.y, 9, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(56, 189, 248, 0.5)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      });
    }

    // 6. Draw 200 Segments in Segments Mode
    if (mode === 'segments' && segments.length > 0) {
      const nSeg = segments.length;

      // Draw connecting lines between consecutive static segments
      for (let k = 0; k < nSeg; k++) {
        const seg = segments[k];
        const nextSeg = segments[(k + 1) % nSeg];
        const p0 = gpsToCanvas(seg.lat, seg.lng, width, height);
        const p1 = gpsToCanvas(nextSeg.lat, nextSeg.lng, width, height);

        const isSelected = selectedSegmentIndex === k;
        const isHovered = hoveredSegmentIndex === k;

        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.lineTo(p1.x, p1.y);
        if (isSelected) {
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 6;
        } else if (isHovered) {
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 5;
        } else {
          ctx.strokeStyle = k % 2 === 0 ? 'rgba(56, 189, 248, 0.85)' : 'rgba(129, 140, 248, 0.85)';
          ctx.lineWidth = 3.5;
        }
        ctx.stroke();
      }

      // Draw segment points & milestone labels
      for (let k = 0; k < nSeg; k++) {
        const seg = segments[k];
        const c = gpsToCanvas(seg.lat, seg.lng, width, height);
        const isSelected = selectedSegmentIndex === k;
        const isHovered = hoveredSegmentIndex === k;
        const isMilestone = k % 25 === 0;

        if (isSelected) {
          ctx.beginPath();
          ctx.arc(c.x, c.y, 8, 0, Math.PI * 2);
          ctx.fillStyle = '#f59e0b';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(c.x, c.y, 15, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)';
          ctx.lineWidth = 2;
          ctx.stroke();

          const label = `Seg #${k} (${seg.distanceM?.toFixed(0)}m)`;
          ctx.font = 'bold 11px Inter, sans-serif';
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = 'rgba(15, 23, 42, 0.95)';
          ctx.fillRect(c.x - tw / 2 - 6, c.y - 28, tw + 12, 20);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1;
          ctx.strokeRect(c.x - tw / 2 - 6, c.y - 28, tw + 12, 20);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, c.x, c.y - 18);
        } else if (isHovered) {
          ctx.beginPath();
          ctx.arc(c.x, c.y, 6, 0, Math.PI * 2);
          ctx.fillStyle = '#38bdf8';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.5;
          ctx.stroke();

          const label = `Seg #${k}`;
          ctx.font = 'bold 10px Inter, sans-serif';
          const tw = ctx.measureText(label).width;
          ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
          ctx.fillRect(c.x - tw / 2 - 4, c.y - 22, tw + 8, 16);
          ctx.strokeStyle = '#38bdf8';
          ctx.lineWidth = 1;
          ctx.strokeRect(c.x - tw / 2 - 4, c.y - 22, tw + 8, 16);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, c.x, c.y - 14);
        } else if (isMilestone) {
          ctx.beginPath();
          ctx.arc(c.x, c.y, k === 0 ? 6 : 4.5, 0, Math.PI * 2);
          ctx.fillStyle = k === 0 ? '#22c55e' : '#a855f7';
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.2;
          ctx.stroke();

          const mLabel = k === 0 ? '🏁 #0' : `#${k}`;
          ctx.font = 'bold 9px Inter, sans-serif';
          const tw = ctx.measureText(mLabel).width;
          ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
          ctx.fillRect(c.x - tw / 2 - 3, c.y - 18, tw + 6, 13);
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(mLabel, c.x, c.y - 12);
        } else {
          ctx.beginPath();
          ctx.arc(c.x, c.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
          ctx.fill();
        }
      }
    }
  }, [
    showSatellite,
    showCenterline,
    showBoundaries,
    showAnnotations,
    satelliteImage,
    imageMetadata,
    controlPoints,
    trackWidthM,
    innerBounds,
    outerBounds,
    splineSamples,
    annotations,
    segments,
    selectedSegmentIndex,
    hoveredSegmentIndex,
    selectedPointIndices,
    selectedSet,
    marqueeBox,
    selectedAnnotationId,
    mode,
    viewport,
    gpsToCanvas,
    latitude,
    longitude,
    zoom,
    renderDynamicGoogleTiles,
    tileRenderTrigger,
  ]);

  // Pointer interactions on canvas
  const startDrag = (target: NonNullable<typeof dragTargetRef.current>, e: React.PointerEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = true;
    dragTargetRef.current = target;
    setIsDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ignore pointer capture errors
    }
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Alt key, space+drag, middle click, or right click for panning
    if (e.altKey || e.button === 1 || e.buttons === 4 || e.button === 2) {
      startDrag({
        type: 'pan',
        startX: cx,
        startY: cy,
      }, e);
      return;
    }

    // 1. Check if clicking on an annotation or handle in Corners or Sectors mode
    if (mode === 'corners' || mode === 'sectors') {
      setContextMenu(null);

      for (const a of annotations) {
        if (mode === 'corners' && a.type === 'corner') {
          const entryPt = a.entry || getPointAtT(0, controlPoints);
          const apexPt = a.apex || getPointAtT(0, controlPoints);
          const exitPt = a.exit || getPointAtT(0, controlPoints);

          const cEntry = gpsToCanvas(entryPt.lat, entryPt.lng, width, height);
          const cApex = gpsToCanvas(apexPt.lat, apexPt.lng, width, height);
          const cExit = gpsToCanvas(exitPt.lat, exitPt.lng, width, height);

          // Check Entry handle
          if (Math.hypot(cx - cEntry.x, cy - cEntry.y) < 12) {
            setSelectedAnnotationId(a.id!);
            setSelectedHandle('entry');
            startDrag({
              type: 'annotation-handle',
              annotId: a.id,
              handleType: 'entry',
              startX: cx,
              startY: cy,
            }, e);
            return;
          }

          // Check Exit handle
          if (Math.hypot(cx - cExit.x, cy - cExit.y) < 12) {
            setSelectedAnnotationId(a.id!);
            setSelectedHandle('exit');
            startDrag({
              type: 'annotation-handle',
              annotId: a.id,
              handleType: 'exit',
              startX: cx,
              startY: cy,
            }, e);
            return;
          }

          // Check Apex / Whole corner drag
          if (Math.hypot(cx - cApex.x, cy - cApex.y) < 18) {
            const cursorGPS = canvasToGPS(cx, cy, width, height);
            const clickT = findClosestParametricT(cursorGPS, splineSamples).t;
            const entryT = a.entry ? findClosestParametricT(a.entry, splineSamples).t : 0;
            const apexT = a.apex ? findClosestParametricT(a.apex, splineSamples).t : 0;
            const exitT = a.exit ? findClosestParametricT(a.exit, splineSamples).t : 0;

            setSelectedAnnotationId(a.id!);
            setSelectedHandle('apex');
            startDrag({
              type: 'annotation-whole',
              annotId: a.id,
              handleType: 'apex',
              startX: cx,
              startY: cy,
              startPositions: {
                entryT,
                apexT,
                exitT,
                startMouseT: clickT,
              },
            }, e);
            return;
          }
        } else if (mode === 'sectors' && (a.type === 'sector' || a.type === 'finish_line')) {
          const pt = a.location || getPointAtT(0, controlPoints);
          const cPt = gpsToCanvas(pt.lat, pt.lng, width, height);

          if (Math.hypot(cx - cPt.x, cy - cPt.y) < 16) {
            setSelectedAnnotationId(a.id!);
            setSelectedHandle('marker');
            startDrag({
              type: 'annotation-handle',
              annotId: a.id,
              handleType: 'marker',
              startX: cx,
              startY: cy,
            }, e);
            return;
          }
        }
      }

      // Check if clicking on empty track shape to add corner or sector or move finish line
      if (controlPoints.length >= 3 && splineSamples.length > 2) {
        const clickGPS = canvasToGPS(cx, cy, width, height);
        const closest = findClosestParametricT(clickGPS, splineSamples);
        const maxHitDistM = Math.max(14.0, (trackWidthM || 8.0) * 1.5);
        if (closest.distanceToCurveM <= maxHitDistM) {
          const selected = annotations.find(a => a.id === selectedAnnotationId);
          if (selected?.type === 'finish_line') {
            updateSelectedAnnotation({
              location: closest.point,
            });
            return;
          }

          if (mode === 'corners') {
            addCornerAtT(closest.t);
            return;
          } else if (mode === 'sectors') {
            addSectorAtT(closest.t);
            return;
          }
        }
      }

      // If clicking empty canvas in Corners/Sectors mode, deselect
      setSelectedAnnotationId(null);
      setSelectedHandle(null);
    }

    // 1b. Check if clicking on a segment in Segments mode
    if (mode === 'segments' && segments.length > 0) {
      const cursorGps = canvasToGPS(cx, cy, width, height);
      const closestIdx = findClosestSegmentIndex(cursorGps, segments);
      if (closestIdx >= 0) {
        const segPt = segments[closestIdx];
        const segCanvas = gpsToCanvas(segPt.lat, segPt.lng, width, height);
        if (Math.hypot(cx - segCanvas.x, cy - segCanvas.y) <= 25) {
          setSelectedSegmentIndex(closestIdx);
          return;
        }
      }
    }

    // 2. Check if clicking on a control point in Spline mode
    if (mode === 'spline') {
      for (let i = 0; i < controlPoints.length; i++) {
        const c = gpsToCanvas(controlPoints[i].lat, controlPoints[i].lng, width, height);
        if (Math.hypot(cx - c.x, cy - c.y) < 12) {
          let newSelected: number[];
          if (e.shiftKey || e.metaKey || e.ctrlKey) {
            if (selectedSet.has(i)) {
              newSelected = selectedPointIndices.filter(idx => idx !== i);
            } else {
              newSelected = [...selectedPointIndices, i];
            }
          } else {
            if (selectedSet.has(i) && selectedPointIndices.length > 1) {
              newSelected = selectedPointIndices;
            } else {
              newSelected = [i];
            }
          }
          setSelectedPointIndices(newSelected);

          const initialPositions = newSelected.map(idx => ({
            idx,
            lat: controlPoints[idx].lat,
            lng: controlPoints[idx].lng,
          }));

          startDrag({
            type: 'points',
            dragPointIndex: i,
            startX: cx,
            startY: cy,
            startLat: controlPoints[i].lat,
            startLng: controlPoints[i].lng,
            initialPositions,
          }, e);
          return;
        }
      }

      // 3. Check if clicking directly on the centerline to insert a new point
      const clickGPS = canvasToGPS(cx, cy, width, height);
      if (controlPoints.length >= 3 && splineSamples.length > 2) {
        const closest = findClosestParametricT(clickGPS, splineSamples);
        const cClosest = gpsToCanvas(closest.point.lat, closest.point.lng, width, height);
        const screenDist = Math.hypot(cx - cClosest.x, cy - cClosest.y);

        // Only insert a new spline node if clicking directly on the track line (within 12px screen radius)
        if (screenDist <= 12) {
          const insertIdx = Math.floor(closest.t * controlPoints.length);
          const newPoints = [...controlPoints];
          newPoints.splice(insertIdx + 1, 0, closest.point);
          setControlPoints(newPoints);
          setSelectedPointIndices([insertIdx + 1]);
          pushState(newPoints, trackWidthM, annotations);
          return;
        }
      }

      // Exception: If empty canvas / less than 3 nodes, create initial points anywhere on canvas
      if (controlPoints.length < 3) {
        const newPoints = [...controlPoints, clickGPS];
        setControlPoints(newPoints);
        setSelectedPointIndices([newPoints.length - 1]);
        pushState(newPoints, trackWidthM, annotations);
        return;
      }

      // If dragging on empty canvas in Spline mode without Alt key: start marquee selection!
      if (!e.altKey) {
        const isModifier = e.shiftKey || e.metaKey || e.ctrlKey;
        if (!isModifier) {
          setSelectedPointIndices([]);
        }
        startDrag({
          type: 'marquee',
          startX: cx,
          startY: cy,
          isShift: isModifier,
          initialSelected: isModifier ? selectedPointIndices : [],
        }, e);
        return;
      }
    }

    // Otherwise, pan canvas
    startDrag({
      type: 'pan',
      startX: cx,
      startY: cy,
    }, e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (!isDraggingRef.current || !dragTargetRef.current) {
      if (mode === 'segments' && segments.length > 0) {
        const cursorGps = canvasToGPS(cx, cy, width, height);
        const closestIdx = findClosestSegmentIndex(cursorGps, segments);
        if (closestIdx >= 0) {
          const segPt = segments[closestIdx];
          const segCanvas = gpsToCanvas(segPt.lat, segPt.lng, width, height);
          if (Math.hypot(cx - segCanvas.x, cy - segCanvas.y) <= 25) {
            setHoveredSegmentIndex(closestIdx);
          } else {
            setHoveredSegmentIndex(null);
          }
        }
      }
      return;
    }

    const target = dragTargetRef.current;

    if (target.type === 'pan') {
      const dx = cx - target.startX;
      const dy = cy - target.startY;
      setViewport(v => ({ ...v, offsetX: v.offsetX + dx, offsetY: v.offsetY + dy }));
      target.startX = cx;
      target.startY = cy;
    } else if (target.type === 'points' && target.initialPositions) {
      const newGPS = canvasToGPS(cx, cy, width, height);
      const dLat = newGPS.lat - target.startLat!;
      const dLng = newGPS.lng - target.startLng!;

      setControlPoints(prev => {
        const updated = [...prev];
        for (const p of target.initialPositions!) {
          updated[p.idx] = {
            ...updated[p.idx],
            lat: p.lat + dLat,
            lng: p.lng + dLng,
          };
        }
        return updated;
      });
    } else if (target.type === 'marquee') {
      const x1 = Math.min(target.startX, cx);
      const y1 = Math.min(target.startY, cy);
      const x2 = Math.max(target.startX, cx);
      const y2 = Math.max(target.startY, cy);
      setMarqueeBox({ x1, y1, x2, y2 });

      const enclosed = controlPoints
        .map((p, idx) => ({ ...gpsToCanvas(p.lat, p.lng, width, height), idx }))
        .filter(pt => pt.x >= x1 && pt.x <= x2 && pt.y >= y1 && pt.y <= y2)
        .map(pt => pt.idx);

      if (target.isShift && target.initialSelected) {
        const union = Array.from(new Set([...target.initialSelected, ...enclosed]));
        setSelectedPointIndices(union);
      } else {
        setSelectedPointIndices(enclosed);
      }
    } else if (target.type === 'annotation-handle' && target.annotId) {
      const cursorGPS = canvasToGPS(cx, cy, width, height);
      const closest = findClosestParametricT(cursorGPS, splineSamples);
      const snapped = closest.point;

      setAnnotations(prev => {
        const updated = prev.map(a => {
          if (a.id !== target.annotId) return a;

          if (a.type === 'corner') {
            if (target.handleType === 'entry') {
              return { ...a, entry: snapped };
            } else if (target.handleType === 'exit') {
              return { ...a, exit: snapped };
            }
          }
          return { ...a, location: snapped };
        });
        return renumberCornersByFinishLine(updated, splineSamples);
      });
    } else if (target.type === 'annotation-whole' && target.annotId && target.startPositions) {
      const cursorGPS = canvasToGPS(cx, cy, width, height);
      const closest = findClosestParametricT(cursorGPS, splineSamples);

      const startRefT = target.startPositions.startMouseT ?? target.startPositions.apexT;
      const deltaT = closest.t - startRefT;

      const entryT = ((target.startPositions.entryT + deltaT) % 1.0 + 1.0) % 1.0;
      const apexT = ((target.startPositions.apexT + deltaT) % 1.0 + 1.0) % 1.0;
      const exitT = ((target.startPositions.exitT + deltaT) % 1.0 + 1.0) % 1.0;

      const entryPt = getPointAtT(entryT, controlPoints);
      const apexPt = getPointAtT(apexT, controlPoints);
      const exitPt = getPointAtT(exitT, controlPoints);
      const dir = inferTurnDirectionAtT(apexT, splineSamples);

      setAnnotations(prev => {
        const updated = prev.map(a => {
          if (a.id !== target.annotId) return a;
          return { ...a, entry: entryPt, apex: apexPt, exit: exitPt, direction: a.direction || dir };
        });
        return renumberCornersByFinishLine(updated, splineSamples);
      });
    }
  };

  const handlePointerUp = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (e) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Ignore
      }
    }
    if (isDraggingRef.current && dragTargetRef.current) {
      if (dragTargetRef.current.type === 'points') {
        pushState(controlPoints, trackWidthM, annotations);
      } else if (dragTargetRef.current.type !== 'pan' && dragTargetRef.current.type !== 'marquee') {
        const renumbered = renumberCornersByFinishLine(annotations, splineSamples);
        setAnnotations(renumbered);
        pushState(controlPoints, trackWidthM, renumbered);
      }
    }
    setMarqueeBox(null);
    isDraggingRef.current = false;
    dragTargetRef.current = null;
    setIsDragging(false);
  };

  // Non-passive wheel event listener for smooth zooming centered on current screen view
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      setViewport(v => {
        const nextScale = Math.max(20000, Math.min(1000000, v.scale * zoomFactor));
        const actualFactor = nextScale / v.scale;
        return {
          ...v,
          scale: nextScale,
          offsetX: v.offsetX * actualFactor,
          offsetY: v.offsetY * actualFactor,
        };
      });
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      canvas.removeEventListener('wheel', onWheel);
    };
  }, []);

  const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg-surface text-text-primary">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border-subtle bg-bg-surface shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/40 flex items-center justify-center text-accent">
            <Compass size={18} />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight">{trackName} — {t('trackStudio.title')}</h2>
            <p className="text-xs text-text-muted">{t('trackStudio.subtitle')}</p>
          </div>
        </div>

        {/* Mode Selector */}
        <div className="flex items-center bg-bg-base rounded-lg p-1 border border-border-subtle">
          <button
            onClick={() => {
              setMode('spline');
              setSelectedAnnotationId(null);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              mode === 'spline' ? 'bg-accent text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Sliders size={14} />
            {t('trackStudio.modeSpline')}
          </button>
          <button
            onClick={() => {
              setMode('corners');
              setSelectedPointIndices([]);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              mode === 'corners' ? 'bg-accent text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <CornerDownRight size={14} />
            {t('trackStudio.modeCorners')}
          </button>
          <button
            onClick={() => {
              setMode('sectors');
              setSelectedPointIndices([]);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              mode === 'sectors' ? 'bg-accent text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Flag size={14} />
            {t('trackStudio.modeSectors')}
          </button>
          <button
            onClick={() => {
              setMode('segments');
              setSelectedPointIndices([]);
              setSelectedAnnotationId(null);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              mode === 'segments' ? 'bg-accent text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Grid size={14} />
            {t('trackStudio.modeSegments')}
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {/* Undo / Redo */}
          <div className="flex items-center border-l border-r border-border-subtle px-2 gap-1">
            <button
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              title={t('trackStudio.undo')}
              className="p-1.5 rounded hover:bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-30 cursor-pointer"
            >
              <Undo2 size={15} />
            </button>
            <button
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              title={t('trackStudio.redo')}
              className="p-1.5 rounded hover:bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-30 cursor-pointer"
            >
              <Redo2 size={15} />
            </button>
          </div>

          {/* Save Button */}
          <button
            onClick={() => handleSave(false)}
            disabled={saving || !hasChanges || controlPoints.length < 3}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-semibold bg-accent hover:bg-accent-hover text-white transition-colors shadow-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save size={14} />
            {saving ? t('common.loading') : t('common.save')}
          </button>

          <button
            onClick={() => {
              if (hasChanges) {
                if (window.confirm(t('trackStudio.closeConfirm'))) {
                  onClose();
                }
              } else {
                onClose();
              }
            }}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
            title={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main Studio Body */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Left / Center Viewport Canvas */}
        <div ref={containerRef} className="flex-1 relative overflow-hidden bg-bg-base select-none">
          <canvas
            ref={canvasRef}
            className={`w-full h-full touch-none ${altPressed ? 'cursor-grab' : 'cursor-crosshair'}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />

          {/* Floating Viewport Controls */}
          <div className="absolute top-4 left-4 flex flex-col gap-1 bg-bg-surface/90 backdrop-blur border border-border-subtle rounded-lg p-1 shadow-lg">
            <button
              onClick={() =>
                setViewport(v => {
                  const nextScale = Math.min(1000000, v.scale * 1.25);
                  const actualFactor = nextScale / v.scale;
                  return {
                    ...v,
                    scale: nextScale,
                    offsetX: v.offsetX * actualFactor,
                    offsetY: v.offsetY * actualFactor,
                  };
                })
              }
              title={t('trackStudio.zoomIn')}
              className="p-2 rounded hover:bg-bg-hover text-text-secondary hover:text-text-primary cursor-pointer"
            >
              <ZoomIn size={16} />
            </button>
            <button
              onClick={() =>
                setViewport(v => {
                  const nextScale = Math.max(20000, v.scale * 0.8);
                  const actualFactor = nextScale / v.scale;
                  return {
                    ...v,
                    scale: nextScale,
                    offsetX: v.offsetX * actualFactor,
                    offsetY: v.offsetY * actualFactor,
                  };
                })
              }
              title={t('trackStudio.zoomOut')}
              className="p-2 rounded hover:bg-bg-hover text-text-secondary hover:text-text-primary cursor-pointer"
            >
              <ZoomOut size={16} />
            </button>
            <button
              onClick={() => setViewport(v => ({ ...v, offsetX: 0, offsetY: 0, scale: 120000 }))}
              title={t('trackStudio.resetView')}
              className="p-2 rounded hover:bg-bg-hover text-text-secondary hover:text-text-primary cursor-pointer"
            >
              <Maximize2 size={16} />
            </button>
          </div>

          {/* Floating Layer Visibility Toggles */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-bg-surface/90 backdrop-blur border border-border-subtle rounded-lg px-3 py-1.5 shadow-lg text-xs">
            <Layers size={14} className="text-text-muted mr-1" />
            <button
              onClick={() => setShowSatellite(s => !s)}
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors ${
                showSatellite ? 'bg-accent/20 text-accent font-medium' : 'text-text-muted hover:text-text-primary'
              }`}
            >
              {showSatellite ? <Eye size={12} /> : <EyeOff size={12} />}
              {t('trackStudio.layerSatellite')}
            </button>
            <button
              onClick={() => setShowCenterline(s => !s)}
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors ${
                showCenterline ? 'bg-amber-500/20 text-amber-400 font-medium' : 'text-text-muted hover:text-text-primary'
              }`}
            >
              {showCenterline ? <Eye size={12} /> : <EyeOff size={12} />}
              {t('trackStudio.layerCenterline')}
            </button>
            <button
              onClick={() => setShowBoundaries(s => !s)}
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors ${
                showBoundaries ? 'bg-sky-500/20 text-sky-400 font-medium' : 'text-text-muted hover:text-text-primary'
              }`}
            >
              {showBoundaries ? <Eye size={12} /> : <EyeOff size={12} />}
              {t('trackStudio.layerBoundaries')}
            </button>
            <button
              onClick={() => setShowAnnotations(s => !s)}
              className={`flex items-center gap-1 px-2 py-1 rounded cursor-pointer transition-colors ${
                showAnnotations ? 'bg-red-500/20 text-red-400 font-medium' : 'text-text-muted hover:text-text-primary'
              }`}
            >
              {showAnnotations ? <Eye size={12} /> : <EyeOff size={12} />}
              {t('trackStudio.layerAnnotations')}
            </button>
          </div>

          {/* Circuit Length Badge */}
          <div className="absolute top-4 right-4 bg-bg-surface/90 backdrop-blur border border-border-subtle rounded-lg px-3 py-1.5 shadow-lg text-xs font-mono text-text-primary">
            {t('trackStudio.trackLength', { length: Math.round(totalLengthM).toLocaleString() })}
          </div>

          {/* Floating Context Menu on Track Click */}
          {contextMenu && (
            <>
              {/* Backdrop click to dismiss */}
              <div
                className="absolute inset-0 z-30"
                onClick={() => setContextMenu(null)}
                onContextMenu={e => {
                  e.preventDefault();
                  setContextMenu(null);
                }}
              />
              <div
                className="absolute z-40 bg-bg-surface/95 backdrop-blur-md border border-border-subtle rounded-xl shadow-2xl p-1.5 min-w-[210px] flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-100"
                style={{
                  left: Math.max(12, Math.min(contextMenu.x + 10, (canvasRef.current?.clientWidth || 600) - 230)),
                  top: Math.max(12, Math.min(contextMenu.y + 10, (canvasRef.current?.clientHeight || 400) - 170)),
                }}
                onClick={e => e.stopPropagation()}
              >
                <div className="px-2.5 py-1.5 border-b border-border-subtle/60 flex items-center justify-between text-[11px] text-text-muted">
                  <span className="font-semibold uppercase tracking-wider">{t('trackStudio.menuTrackPosition')}</span>
                  <span className="font-mono text-accent font-medium">{(contextMenu.t * 100).toFixed(2)}%</span>
                </div>
                <button
                  onClick={() => {
                    addCornerAtT(contextMenu.t);
                    setContextMenu(null);
                  }}
                  className="w-full px-2.5 py-2 rounded-lg text-left text-xs font-medium text-text-primary hover:bg-accent/15 hover:text-accent flex items-center gap-2.5 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 font-bold text-[10px] flex items-center justify-center">
                    T
                  </span>
                  <span>{t('trackStudio.menuAddCorner')}</span>
                </button>
                <button
                  onClick={() => {
                    addSectorAtT(contextMenu.t);
                    setContextMenu(null);
                  }}
                  className="w-full px-2.5 py-2 rounded-lg text-left text-xs font-medium text-text-primary hover:bg-purple-500/15 hover:text-purple-400 flex items-center gap-2.5 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 rounded bg-purple-500/20 text-purple-400 font-bold text-[10px] flex items-center justify-center">
                    S
                  </span>
                  <span>{t('trackStudio.menuAddSector')}</span>
                </button>
                <button
                  onClick={() => {
                    setFinishLineAtT(contextMenu.t);
                    setContextMenu(null);
                  }}
                  className="w-full px-2.5 py-2 rounded-lg text-left text-xs font-medium text-text-primary hover:bg-blue-500/15 hover:text-blue-400 flex items-center gap-2.5 transition-colors cursor-pointer"
                >
                  <span className="text-sm">🏁</span>
                  <span>{t('trackStudio.menuSetFinishLine')}</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar Inspector */}
        <div className="w-80 border-l border-border-subtle bg-bg-surface flex flex-col shrink-0 overflow-y-auto">
          {mode === 'spline' ? (
            <div className="p-5 space-y-6">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
                  {t('trackStudio.controlPoints', { count: String(controlPoints.length) })}
                </h3>
                <p className="text-xs text-text-secondary leading-relaxed mb-4">
                  {t('trackStudio.addPointHint')}
                </p>

                {/* Global Track Width Control */}
                <div className="space-y-2 mb-4 p-3 bg-bg-base rounded-lg border border-border-subtle">
                  <div className="flex justify-between text-xs">
                    <span className="text-text-muted">{t('trackStudio.defaultTrackWidth')}</span>
                    <span className="font-semibold text-text-primary">{trackWidthM.toFixed(1)} m</span>
                  </div>
                  <input
                    type="range"
                    min="4"
                    max="18"
                    step="0.5"
                    value={trackWidthM}
                    onChange={e => {
                      const w = parseFloat(e.target.value);
                      setTrackWidthM(w);
                      pushState(controlPoints, w, annotations);
                    }}
                    className="w-full accent-accent cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-text-muted">
                    <span>4.0 m</span>
                    <span>11.0 m</span>
                    <span>18.0 m</span>
                  </div>
                </div>

                {/* Segment Width Tool Accordion */}
                <div className="mb-4 p-3 bg-bg-base rounded-lg border border-border-subtle space-y-3">
                  <button
                    onClick={() => setSegmentToolOpen(o => !o)}
                    className="w-full flex items-center justify-between text-xs font-semibold text-text-primary cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5">
                      <Sliders size={13} className="text-accent" />
                      <span>{t('trackStudio.segmentWidth')}</span>
                    </div>
                    <span className="text-[10px] text-accent font-medium">
                      {segmentToolOpen ? '▲' : '▼'}
                    </span>
                  </button>

                  {segmentToolOpen && (
                    <div className="space-y-3 pt-2 border-t border-border-subtle text-xs">
                      <p className="text-[11px] text-text-secondary">
                        {t('trackStudio.segmentWidthDesc')}
                      </p>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] uppercase font-semibold text-text-muted">
                            {t('trackStudio.fromPoint')}
                          </label>
                          <select
                            value={segFrom}
                            onChange={e => setSegFrom(parseInt(e.target.value))}
                            className="w-full mt-1 px-2 py-1 bg-bg-surface border border-border-subtle rounded text-text-primary"
                          >
                            {controlPoints.map((_, i) => (
                              <option key={i} value={i + 1}>
                                #{i + 1}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase font-semibold text-text-muted">
                            {t('trackStudio.toPoint')}
                          </label>
                          <select
                            value={segTo}
                            onChange={e => setSegTo(parseInt(e.target.value))}
                            className="w-full mt-1 px-2 py-1 bg-bg-surface border border-border-subtle rounded text-text-primary"
                          >
                            {controlPoints.map((_, i) => (
                              <option key={i} value={i + 1}>
                                #{i + 1}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Transition Mode Selector */}
                      <div>
                        <label className="text-[10px] uppercase font-semibold text-text-muted">
                          {t('trackStudio.transitionMode')}
                        </label>
                        <div className="grid grid-cols-2 gap-1.5 mt-1">
                          <button
                            onClick={() => setSegMode('constant')}
                            className={`py-1 px-2 rounded text-[11px] font-medium border transition-colors cursor-pointer ${
                              segMode === 'constant'
                                ? 'bg-accent text-white border-accent'
                                : 'bg-bg-surface text-text-secondary border-border-subtle hover:text-text-primary'
                            }`}
                          >
                            {t('trackStudio.modeConstant')}
                          </button>
                          <button
                            onClick={() => setSegMode('gradient')}
                            className={`py-1 px-2 rounded text-[11px] font-medium border transition-colors cursor-pointer ${
                              segMode === 'gradient'
                                ? 'bg-accent text-white border-accent'
                                : 'bg-bg-surface text-text-secondary border-border-subtle hover:text-text-primary'
                            }`}
                          >
                            {t('trackStudio.modeGradient')}
                          </button>
                        </div>
                      </div>

                      {/* Width Sliders */}
                      {segMode === 'constant' ? (
                        <div className="space-y-1">
                          <div className="flex justify-between text-[11px]">
                            <span className="text-text-muted">{t('trackStudio.trackWidth')}</span>
                            <span className="font-semibold text-text-primary">{segStartWidth.toFixed(1)} m</span>
                          </div>
                          <input
                            type="range"
                            min="4"
                            max="20"
                            step="0.5"
                            value={segStartWidth}
                            onChange={e => setSegStartWidth(parseFloat(e.target.value))}
                            className="w-full accent-accent cursor-pointer"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-text-muted">{t('trackStudio.startWidth')}</span>
                              <span className="font-semibold text-text-primary">{segStartWidth.toFixed(1)} m</span>
                            </div>
                            <input
                              type="range"
                              min="4"
                              max="20"
                              step="0.5"
                              value={segStartWidth}
                              onChange={e => setSegStartWidth(parseFloat(e.target.value))}
                              className="w-full accent-accent cursor-pointer"
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-text-muted">{t('trackStudio.endWidth')}</span>
                              <span className="font-semibold text-text-primary">{segEndWidth.toFixed(1)} m</span>
                            </div>
                            <input
                              type="range"
                              min="4"
                              max="20"
                              step="0.5"
                              value={segEndWidth}
                              onChange={e => setSegEndWidth(parseFloat(e.target.value))}
                              className="w-full accent-accent cursor-pointer"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handleApplySegmentWidth}
                          disabled={controlPoints.length < 3}
                          className="flex-1 py-1.5 px-2.5 rounded bg-accent hover:bg-accent-hover text-white text-xs font-semibold transition-colors cursor-pointer disabled:opacity-40"
                        >
                          {t('trackStudio.applySegment')}
                        </button>
                        <button
                          onClick={handleResetSegmentWidth}
                          disabled={controlPoints.length < 3}
                          className="py-1.5 px-2.5 rounded bg-bg-surface hover:bg-bg-hover text-text-secondary border border-border-subtle text-xs font-medium transition-colors cursor-pointer disabled:opacity-40"
                          title={t('trackStudio.resetSegment')}
                        >
                          <RotateCcw size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Spline Tools */}
                <div className="space-y-2 mb-4">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setSelectedPointIndices(controlPoints.map((_, i) => i))}
                      disabled={controlPoints.length === 0}
                      className="px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-primary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 cursor-pointer"
                      title={t('trackStudio.selectAll')}
                    >
                      <CheckSquare size={13} className="text-accent" />
                      <span>{t('trackStudio.selectAll')}</span>
                    </button>
                    {selectedPointIndices.length > 0 ? (
                      <button
                        onClick={() => setSelectedPointIndices([])}
                        className="px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-secondary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                        title={t('trackStudio.deselectAll')}
                      >
                        <X size={13} />
                        <span>{t('trackStudio.deselectAll')}</span>
                      </button>
                    ) : (
                      <button
                        onClick={evenlySpacePoints}
                        disabled={controlPoints.length < 4}
                        className="px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-primary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 cursor-pointer"
                      >
                        <Sliders size={13} />
                        <span>{t('trackStudio.evenSpacePoints')}</span>
                      </button>
                    )}
                  </div>

                  {selectedPointIndices.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={evenlySpacePoints}
                        disabled={controlPoints.length < 4}
                        className="px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-primary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 cursor-pointer"
                      >
                        <Sliders size={13} />
                        <span>{t('trackStudio.evenSpacePoints')}</span>
                      </button>
                      <button
                        onClick={reverseDirection}
                        disabled={controlPoints.length < 3}
                        className="px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-primary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 cursor-pointer"
                      >
                        <ArrowRightLeft size={13} />
                        <span>{t('trackStudio.reverseDirection')}</span>
                      </button>
                    </div>
                  )}

                  {selectedPointIndices.length === 0 && (
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        onClick={reverseDirection}
                        disabled={controlPoints.length < 3}
                        className="w-full px-2.5 py-1.5 text-xs font-medium rounded bg-bg-base hover:bg-bg-hover text-text-primary border border-border-subtle transition-colors flex items-center justify-center gap-1.5 disabled:opacity-40 cursor-pointer"
                      >
                        <ArrowRightLeft size={13} />
                        <span>{t('trackStudio.reverseDirection')}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Multi-point Selection Inspector */}
              {selectedPointIndices.length > 1 && (
                <div className="p-4 bg-bg-base rounded-lg border border-border-subtle space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-accent/20 text-accent font-bold text-[10px] flex items-center justify-center">
                        {selectedPointIndices.length}
                      </span>
                      <span className="text-xs font-semibold text-text-primary">
                        {t('trackStudio.multiplePointsSelected', { count: String(selectedPointIndices.length) })}
                      </span>
                    </div>
                    <button
                      onClick={deleteSelectedPoints}
                      disabled={controlPoints.length - selectedPointIndices.length < 3}
                      className="text-red hover:text-red-hover p-1 rounded transition-colors disabled:opacity-30 cursor-pointer flex items-center gap-1 text-xs"
                      title={t('trackStudio.deleteSelectedPoints', { count: String(selectedPointIndices.length) })}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Multi-point Custom Width Override */}
                  <div className="pt-2 border-t border-border-subtle space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-medium text-text-primary flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPointIndices.some(idx => controlPoints[idx]?.widthM !== undefined && controlPoints[idx]?.widthM! > 0)}
                          onChange={e => {
                            if (e.target.checked) {
                              handleUpdatePointWidth(trackWidthM);
                            } else {
                              handleUpdatePointWidth(undefined);
                            }
                          }}
                          className="rounded accent-accent cursor-pointer"
                        />
                        <span>{t('trackStudio.customWidthSelection')}</span>
                      </label>
                      <button
                        onClick={() => handleUpdatePointWidth(undefined)}
                        className="text-[10px] text-accent hover:underline cursor-pointer"
                      >
                        {t('trackStudio.resetPointWidth')}
                      </button>
                    </div>

                    <div className="space-y-1.5 pl-5">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-text-muted">{t('trackStudio.pointWidth')}</span>
                        <span className="font-semibold text-sky-400 font-mono">
                          {(controlPoints[selectedPointIndices[0]]?.widthM ?? trackWidthM).toFixed(1)} m
                        </span>
                      </div>
                      <input
                        type="range"
                        min="4"
                        max="20"
                        step="0.2"
                        value={controlPoints[selectedPointIndices[0]]?.widthM ?? trackWidthM}
                        onChange={e => handleUpdatePointWidth(parseFloat(e.target.value))}
                        className="w-full accent-sky-400 cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-border-subtle flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {selectedPointIndices.map(idx => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-bg-surface border border-border-subtle text-[10px] font-mono text-text-secondary"
                      >
                        #{idx + 1}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Single Selected Point Inspector */}
              {selectedPointIndex !== null && controlPoints[selectedPointIndex] && (
                <div className="p-4 bg-bg-base rounded-lg border border-border-subtle space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-text-primary">
                      {t('trackStudio.selectedPoint', { index: String(selectedPointIndex + 1) })}
                    </span>
                    <button
                      onClick={deleteSelectedPoint}
                      disabled={controlPoints.length <= 3}
                      className="text-red hover:text-red-hover p-1 rounded transition-colors disabled:opacity-30 cursor-pointer"
                      title={t('trackStudio.deletePoint')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Individual Point Width Override */}
                  <div className="pt-2 border-t border-border-subtle space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] font-medium text-text-primary flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={controlPoints[selectedPointIndex].widthM !== undefined && controlPoints[selectedPointIndex].widthM! > 0}
                          onChange={e => {
                            if (e.target.checked) {
                              handleUpdatePointWidth(trackWidthM);
                            } else {
                              handleUpdatePointWidth(undefined);
                            }
                          }}
                          className="rounded accent-accent cursor-pointer"
                        />
                        <span>{t('trackStudio.customWidth')}</span>
                      </label>
                      {controlPoints[selectedPointIndex].widthM !== undefined && (
                        <button
                          onClick={() => handleUpdatePointWidth(undefined)}
                          className="text-[10px] text-accent hover:underline cursor-pointer"
                        >
                          {t('trackStudio.resetPointWidth')}
                        </button>
                      )}
                    </div>

                    {controlPoints[selectedPointIndex].widthM !== undefined && (
                      <div className="space-y-1.5 pl-5">
                        <div className="flex justify-between text-[11px]">
                          <span className="text-text-muted">{t('trackStudio.pointWidth')}</span>
                          <span className="font-semibold text-sky-400 font-mono">
                            {controlPoints[selectedPointIndex].widthM!.toFixed(1)} m
                          </span>
                        </div>
                        <input
                          type="range"
                          min="4"
                          max="20"
                          step="0.2"
                          value={controlPoints[selectedPointIndex].widthM!}
                          onChange={e => handleUpdatePointWidth(parseFloat(e.target.value))}
                          className="w-full accent-sky-400 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-border-subtle">
                    <div>
                      <span className="text-text-muted text-[10px]">{t('tracks.latitude').toUpperCase()}</span>
                      <input
                        type="number"
                        step="0.000001"
                        value={controlPoints[selectedPointIndex].lat}
                        onChange={e => {
                          const lat = parseFloat(e.target.value);
                          if (!isNaN(lat)) {
                            const updated = [...controlPoints];
                            updated[selectedPointIndex] = { ...updated[selectedPointIndex], lat };
                            setControlPoints(updated);
                            pushState(updated, trackWidthM, annotations);
                          }
                        }}
                        className="w-full mt-1 px-2 py-1 bg-bg-surface border border-border-subtle rounded text-text-primary font-mono text-[11px]"
                      />
                    </div>
                    <div>
                      <span className="text-text-muted text-[10px]">{t('tracks.longitude').toUpperCase()}</span>
                      <input
                        type="number"
                        step="0.000001"
                        value={controlPoints[selectedPointIndex].lng}
                        onChange={e => {
                          const lng = parseFloat(e.target.value);
                          if (!isNaN(lng)) {
                            const updated = [...controlPoints];
                            updated[selectedPointIndex] = { ...updated[selectedPointIndex], lng };
                            setControlPoints(updated);
                            pushState(updated, trackWidthM, annotations);
                          }
                        }}
                        className="w-full mt-1 px-2 py-1 bg-bg-surface border border-border-subtle rounded text-text-primary font-mono text-[11px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : mode === 'corners' ? (
            <div className="p-5 space-y-6">
              {/* Corners Section Header */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t('trackStudio.cornersCount', {
                      count: String(annotations.filter(a => a.type === 'corner').length),
                    })}
                  </h3>
                </div>

                <div className="p-3 bg-bg-surface/60 rounded-lg border border-border-subtle/70 text-xs mb-4 space-y-1">
                  <p className="font-semibold text-text-primary flex items-center gap-1.5">
                    <CornerDownRight size={13} className="text-accent" />
                    <span>{t('trackStudio.modeCorners')}</span>
                  </p>
                  <p className="text-[11px] leading-relaxed text-text-secondary">
                    {t('trackStudio.clickCornersHint')}
                  </p>
                </div>
              </div>

              {/* Selected Corner Details */}
              {selectedAnnotation && selectedAnnotation.type === 'corner' ? (
                <div className="p-4 bg-bg-base rounded-lg border border-border-subtle space-y-4">
                  <div className="flex items-center justify-between border-b border-border-subtle pb-2">
                    <span className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                      {selectedAnnotation.name}
                    </span>
                    <button
                      onClick={deleteSelectedAnnotation}
                      className="text-red hover:text-red-hover p-1 rounded transition-colors cursor-pointer"
                      title={t('trackStudio.deleteAnnotation')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-text-muted">
                      {t('trackStudio.cornerName')}
                    </label>
                    <input
                      type="text"
                      value={selectedAnnotation.name}
                      onChange={e => updateSelectedAnnotation({ name: e.target.value })}
                      className="w-full mt-1 px-2.5 py-1.5 bg-bg-surface border border-border-subtle rounded text-xs text-text-primary"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-semibold text-text-muted">
                      {t('trackStudio.cornerDirection')}
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        onClick={() => updateSelectedAnnotation({ direction: 'left' })}
                        className={`py-1 px-2 rounded text-xs font-medium border transition-colors cursor-pointer ${
                          selectedAnnotation.direction === 'left'
                            ? 'bg-accent text-white border-accent'
                            : 'bg-bg-surface text-text-secondary border-border-subtle'
                        }`}
                      >
                        {t('trackStudio.directionLeft')}
                      </button>
                      <button
                        onClick={() => updateSelectedAnnotation({ direction: 'right' })}
                        className={`py-1 px-2 rounded text-xs font-medium border transition-colors cursor-pointer ${
                          selectedAnnotation.direction === 'right'
                            ? 'bg-accent text-white border-accent'
                            : 'bg-bg-surface text-text-secondary border-border-subtle'
                        }`}
                      >
                        {t('trackStudio.directionRight')}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-border-subtle">
                    {(() => {
                      const entryPt = selectedAnnotation.entry || getPointAtT(0, controlPoints);
                      const apexPt = selectedAnnotation.apex || getPointAtT(0, controlPoints);
                      const exitPt = selectedAnnotation.exit || getPointAtT(0, controlPoints);

                      const entryT = splineSamples.length > 2 ? findClosestParametricT(entryPt, splineSamples).t : 0;
                      const apexT = splineSamples.length > 2 ? findClosestParametricT(apexPt, splineSamples).t : 0;
                      const exitT = splineSamples.length > 2 ? findClosestParametricT(exitPt, splineSamples).t : 0;

                      return (
                        <>
                          <div className="p-2 bg-bg-surface/80 rounded border border-border-subtle text-[11px] font-mono space-y-0.5">
                            <div className="text-text-muted text-[10px] uppercase font-semibold">{t('trackStudio.geoLocation')}</div>
                            <div className="text-text-primary">
                              LAT: {apexPt.lat.toFixed(6)}, LNG: {apexPt.lng.toFixed(6)}
                            </div>
                          </div>

                          <div>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className="text-orange-400 font-medium">{t('trackStudio.entry')}</span>
                              <span className="text-text-muted font-mono text-[10px]">
                                {entryPt.lat.toFixed(4)}, {entryPt.lng.toFixed(4)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.0001"
                              value={entryT}
                              onChange={e => {
                                const pt = getPointAtT(parseFloat(e.target.value), controlPoints);
                                updateSelectedAnnotation({ entry: pt });
                              }}
                              className="w-full accent-orange-500 cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className="text-red-400 font-medium">{t('trackStudio.apex')}</span>
                              <span className="text-text-muted font-mono text-[10px]">
                                {apexPt.lat.toFixed(4)}, {apexPt.lng.toFixed(4)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.0001"
                              value={apexT}
                              onChange={e => {
                                const pt = getPointAtT(parseFloat(e.target.value), controlPoints);
                                updateSelectedAnnotation({ apex: pt });
                              }}
                              className="w-full accent-red-500 cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className="text-green-400 font-medium">{t('trackStudio.exit')}</span>
                              <span className="text-text-muted font-mono text-[10px]">
                                {exitPt.lat.toFixed(4)}, {exitPt.lng.toFixed(4)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.0001"
                              value={exitT}
                              onChange={e => {
                                const pt = getPointAtT(parseFloat(e.target.value), controlPoints);
                                updateSelectedAnnotation({ exit: pt });
                              }}
                              className="w-full accent-green-500 cursor-pointer"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(() => {
                    const corners = renumberCornersByFinishLine(annotations, splineSamples).filter(
                      a => a.type === 'corner'
                    );
                    return corners.map((a, idx) => {
                      const turnNum = a.orderIndex || idx + 1;
                      return (
                        <div
                          key={a.id}
                          onClick={() => {
                            setSelectedAnnotationId(a.id!);
                            setSelectedHandle('corner');
                          }}
                          className={`p-2.5 rounded-lg border transition-colors cursor-pointer flex items-center justify-between ${
                            a.id === selectedAnnotationId
                              ? 'bg-accent/15 border-accent text-text-primary'
                              : 'bg-bg-base border-border-subtle text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 font-bold text-[10px] flex items-center justify-center">
                              T{turnNum}
                            </span>
                            <span className="text-xs font-medium">{a.name || `Turn ${turnNum}`}</span>
                          </div>
                          {a.apex && (
                            <span className="text-[10px] font-mono text-text-muted">
                              {a.apex.lat.toFixed(4)}, {a.apex.lng.toFixed(4)}
                            </span>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          ) : mode === 'sectors' ? (
            <div className="p-5 space-y-6">
              {/* Sectors Section Header */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t('trackStudio.sectorsCount', {
                      count: String(annotations.filter(a => a.type === 'sector').length),
                    })}
                  </h3>
                </div>

                <div className="p-3 bg-bg-surface/60 rounded-lg border border-border-subtle/70 text-xs mb-4 space-y-1">
                  <p className="font-semibold text-text-primary flex items-center gap-1.5">
                    <Flag size={13} className="text-purple-400" />
                    <span>{t('trackStudio.modeSectors')}</span>
                  </p>
                  <p className="text-[11px] leading-relaxed text-text-secondary">
                    {t('trackStudio.clickSectorsHint')}
                  </p>
                </div>
              </div>

              {/* Selected Sector / Finish Line Details */}
              {selectedAnnotation && (selectedAnnotation.type === 'sector' || selectedAnnotation.type === 'finish_line') ? (
                <div className="p-4 bg-bg-base rounded-lg border border-border-subtle space-y-4">
                  <div className="flex items-center justify-between border-b border-border-subtle pb-2">
                    <span className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                      {selectedAnnotation.type === 'finish_line' && <span>🏁</span>}
                      {selectedAnnotation.name}
                    </span>
                    {selectedAnnotation.type !== 'finish_line' && (
                      <button
                        onClick={deleteSelectedAnnotation}
                        className="text-red hover:text-red-hover p-1 rounded transition-colors cursor-pointer"
                        title={t('trackStudio.deleteAnnotation')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {selectedAnnotation.type === 'finish_line' && (
                    <div className="space-y-4">
                      <div className="p-2.5 rounded bg-accent/10 border border-accent/20 text-xs text-accent space-y-1">
                        <p className="font-semibold flex items-center gap-1.5">
                          <span>🏁</span> {t('trackStudio.finishLineTitle')}
                        </p>
                        <p className="text-[11px] text-text-secondary">
                          {t('trackStudio.finishLineHint')}
                        </p>
                      </div>

                      {(() => {
                        const locPt = selectedAnnotation.location || getPointAtT(0, controlPoints);
                        const locT = splineSamples.length > 2 ? findClosestParametricT(locPt, splineSamples).t : 0;
                        return (
                          <div className="space-y-2 pt-1">
                            <div className="p-2 bg-bg-surface/80 rounded border border-border-subtle text-[11px] font-mono">
                              <span className="text-text-muted text-[10px] uppercase font-semibold block mb-0.5">{t('trackStudio.geoLocation')}</span>
                              <span className="text-text-primary">
                                LAT: {locPt.lat.toFixed(6)}, LNG: {locPt.lng.toFixed(6)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.0001"
                              value={locT}
                              onChange={e => {
                                const pt = getPointAtT(parseFloat(e.target.value), controlPoints);
                                updateSelectedAnnotation({ location: pt });
                              }}
                              className="w-full accent-accent cursor-pointer"
                            />
                          </div>
                        );
                      })()}

                      {/* Driving Direction Control */}
                      <div className="pt-3 border-t border-border-subtle space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] uppercase font-semibold text-text-muted">
                            {t('trackStudio.drivingDirection')}
                          </label>
                        </div>
                        <p className="text-[11px] text-text-secondary">
                          {t('trackStudio.drivingDirectionDesc')}
                        </p>
                        <button
                          onClick={reverseDirection}
                          className="w-full py-2 px-3 bg-bg-surface hover:bg-bg-hover border border-border-subtle rounded-md text-xs font-medium text-text-primary flex items-center justify-center gap-2 transition-colors cursor-pointer"
                        >
                          <RefreshCw size={13} className="text-accent" />
                          <span>{t('trackStudio.reverseDrivingDirection')}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedAnnotation.type === 'sector' && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] uppercase font-semibold text-text-muted">
                          {t('tracks.name')}
                        </label>
                        <input
                          type="text"
                          value={selectedAnnotation.name}
                          onChange={e => updateSelectedAnnotation({ name: e.target.value })}
                          className="w-full mt-1 px-2.5 py-1.5 bg-bg-surface border border-border-subtle rounded text-xs text-text-primary"
                        />
                      </div>

                      {(() => {
                        const locPt = selectedAnnotation.location || getPointAtT(0, controlPoints);
                        const locT = splineSamples.length > 2 ? findClosestParametricT(locPt, splineSamples).t : 0;
                        return (
                          <div className="space-y-2 pt-2 border-t border-border-subtle">
                            <div className="p-2 bg-bg-surface/80 rounded border border-border-subtle text-[11px] font-mono">
                              <span className="text-text-muted text-[10px] uppercase font-semibold block mb-0.5">{t('trackStudio.geoLocation')}</span>
                              <span className="text-text-primary">
                                LAT: {locPt.lat.toFixed(6)}, LNG: {locPt.lng.toFixed(6)}
                              </span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.0001"
                              value={locT}
                              onChange={e => {
                                const pt = getPointAtT(parseFloat(e.target.value), controlPoints);
                                updateSelectedAnnotation({ location: pt });
                              }}
                              className="w-full accent-purple-500 cursor-pointer"
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(() => {
                    const finishLine = annotations.find(a => a.type === 'finish_line');
                    const finishT = finishLine?.location && splineSamples.length > 2
                      ? findClosestParametricT(finishLine.location, splineSamples).t
                      : 0.0;
                    const sectors = annotations.filter(a => a.type === 'sector');
                    sectors.sort((a, b) => {
                      const tA = a.location && splineSamples.length > 2 ? findClosestParametricT(a.location, splineSamples).t : 0;
                      const tB = b.location && splineSamples.length > 2 ? findClosestParametricT(b.location, splineSamples).t : 0;
                      const relA = ((tA - finishT) % 1.0 + 1.0) % 1.0;
                      const relB = ((tB - finishT) % 1.0 + 1.0) % 1.0;
                      return relA - relB;
                    });
                    const list = finishLine ? [finishLine, ...sectors] : sectors;
                    return list.map(a => (
                      <div
                        key={a.id}
                        onClick={() => {
                          setSelectedAnnotationId(a.id!);
                          setSelectedHandle('marker');
                        }}
                        className={`p-2.5 rounded-lg border transition-colors cursor-pointer flex items-center justify-between ${
                          a.id === selectedAnnotationId
                            ? 'bg-accent/15 border-accent text-text-primary'
                            : 'bg-bg-base border-border-subtle text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {a.type === 'finish_line' ? (
                            <span>🏁</span>
                          ) : (
                            <span className="w-5 h-5 rounded bg-purple-500/20 text-purple-400 font-bold text-[10px] flex items-center justify-center">
                              S
                            </span>
                          )}
                          <span className="text-xs font-medium">{a.name}</span>
                        </div>
                        {a.location && (
                          <span className="text-[10px] font-mono text-text-muted">
                            {a.location.lat.toFixed(4)}, {a.location.lng.toFixed(4)}
                          </span>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="p-5 space-y-6">
              {/* Segments Section Header */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t('trackStudio.segmentsCount', {
                      count: String(segments.length),
                    })}
                  </h3>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      segments.length > 0
                        ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                        : 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30'
                    }`}
                  >
                    {segments.length > 0 ? t('trackStudio.segmentsStatusBaked') : t('trackStudio.segmentsStatusNotBaked')}
                  </span>
                </div>

                <div className="p-3 bg-bg-surface/60 rounded-lg border border-border-subtle/70 text-xs mb-4 space-y-1">
                  <p className="font-semibold text-text-primary flex items-center gap-1.5">
                    <Grid size={13} className="text-accent" />
                    <span>{t('trackStudio.modeSegments')}</span>
                  </p>
                  <p className="text-[11px] leading-relaxed text-text-secondary">
                    {t('trackStudio.segmentsHint')}
                  </p>
                </div>

                {/* Bake Button */}
                <button
                  onClick={handleBakeSegments}
                  disabled={controlPoints.length < 3}
                  className="w-full py-2 px-3 bg-accent hover:bg-accent-hover text-white rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                >
                  <Grid size={14} />
                  <span>{segments.length > 0 ? t('trackStudio.rebakeSegments') : t('trackStudio.bakeSegments')}</span>
                </button>
              </div>

              {/* Stats card */}
              {segments.length > 0 && (
                <div className="p-3.5 bg-bg-base rounded-lg border border-border-subtle grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-[10px] uppercase font-semibold text-text-muted block mb-0.5">
                      {t('trackStudio.trackLength')}
                    </span>
                    <span className="font-mono text-xs font-semibold text-text-primary">
                      {totalLengthM.toFixed(1)} m
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-semibold text-text-muted block mb-0.5">
                      {t('trackStudio.avgSegmentLength')}
                    </span>
                    <span className="font-mono text-xs font-semibold text-text-primary">
                      {(totalLengthM / (segments.length || 1)).toFixed(2)} m
                    </span>
                  </div>
                </div>
              )}

              {/* Selected Segment Details */}
              {selectedSegmentIndex !== null && segments[selectedSegmentIndex] && (
                <div className="p-4 bg-bg-base rounded-lg border border-border-subtle space-y-3">
                  <div className="flex items-center justify-between border-b border-border-subtle pb-2">
                    <span className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded bg-accent/20 text-accent font-bold text-[10px] flex items-center justify-center">
                        #{selectedSegmentIndex}
                      </span>
                      {t('trackStudio.segmentIndex', { index: String(selectedSegmentIndex) })}
                    </span>
                    <button
                      onClick={() => setSelectedSegmentIndex(null)}
                      className="text-text-muted hover:text-text-primary p-1 rounded transition-colors cursor-pointer text-[11px]"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  <div className="p-2.5 bg-bg-surface/80 rounded border border-border-subtle text-[11px] font-mono space-y-1">
                    <div className="text-text-muted text-[10px] uppercase font-semibold">{t('trackStudio.staticGpsPosition')}</div>
                    <div className="text-text-primary">
                      LAT: {segments[selectedSegmentIndex].lat.toFixed(6)}
                    </div>
                    <div className="text-text-primary">
                      LNG: {segments[selectedSegmentIndex].lng.toFixed(6)}
                    </div>
                    <div className="text-accent pt-1 border-t border-border-subtle/60">
                      {t('trackStudio.distance')}: {segments[selectedSegmentIndex].distanceM?.toFixed(1)} m
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      onClick={() => setSelectedSegmentIndex((selectedSegmentIndex - 1 + segments.length) % segments.length)}
                      className="py-1 px-2 bg-bg-surface hover:bg-bg-hover border border-border-subtle rounded text-xs text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
                    >
                      ← {t('trackStudio.previous')}
                    </button>
                    <button
                      onClick={() => setSelectedSegmentIndex((selectedSegmentIndex + 1) % segments.length)}
                      className="py-1 px-2 bg-bg-surface hover:bg-bg-hover border border-border-subtle rounded text-xs text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
                    >
                      {t('trackStudio.next')} →
                    </button>
                  </div>
                </div>
              )}

              {/* Segment List with search */}
              {segments.length > 0 && (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('trackStudio.searchSegments')}
                      value={segmentSearchQuery}
                      onChange={e => setSegmentSearchQuery(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-bg-base border border-border-subtle rounded text-xs text-text-primary placeholder:text-text-muted"
                    />
                  </div>

                  <div className="space-y-1 max-h-[340px] overflow-y-auto pr-1">
                    {segments
                      .filter(s => segmentSearchQuery === '' || String(s.index).includes(segmentSearchQuery) || `${s.distanceM}`.includes(segmentSearchQuery))
                      .map(s => (
                        <div
                          key={s.index}
                          onClick={() => setSelectedSegmentIndex(s.index)}
                          className={`p-2 rounded-md border transition-colors cursor-pointer flex items-center justify-between text-xs ${
                            s.index === selectedSegmentIndex
                              ? 'bg-accent/15 border-accent text-text-primary'
                              : 'bg-bg-base border-border-subtle text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded bg-bg-surface text-text-muted font-mono font-bold text-[10px] flex items-center justify-center">
                              {s.index === 0 ? '🏁' : s.index}
                            </span>
                            <span className="font-medium">
                              {t('trackStudio.segmentIndex', { index: String(s.index) })}
                            </span>
                          </div>
                          <span className="font-mono text-[10px] text-text-muted">
                            {s.distanceM?.toFixed(0)} m
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
